import React from 'react';
import { ErrorCatcherProps, ErrorCatcherState, ErrorInfo, ReportError } from './ErrorBoundary.d';
class ErrorBoundary extends React.Component<ErrorCatcherProps, ErrorCatcherState> {
  static getDerivedStateFromError() {
    // react错误边界静态方法，返回值相当于调用setState()
    return {
      hasError: true,
    };
  }
  timeout: any = null;
  selfError = '自身边界错误';
  // 过滤日志
  stableMessage = [
    this.selfError,
    'ResizeObserver loop limit exceeded',
    'Uncaught ReferenceError: viewWillAppear is not defined',
    'Failed to execute "insertBefore" on "Node": The node before which the new node is to be inserted is not a child of this node.',
  ];
  constructor(props: ErrorCatcherProps) {
    super(props);
    this.state = {
      hasError: false,
      maps: new Map(),
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      const obj = {
        caughtEvent: 'componentDidCatch',
        msg: error.message,
        localtime: Date.now(),
        stack: info.componentStack,
        eventType: error.name,
        isTrusted: 1,
        errHref: window.location.href,
      };
      this.postError(obj);
    } catch (error) {
      console.log(`${this.selfError}componentDidCatch`, error);
    }
  }

  componentDidMount() {
    // event catch
    window.addEventListener('error', this.catchError, true);
    // async code
    window.addEventListener('unhandledrejection', this.catchRejectEvent, true);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.catchError, true);
    window.removeEventListener('unhandledrejection', this.catchRejectEvent, true);
  }

  beforeFilter = (error: ErrorInfo): boolean => {
    const judge = this.stableMessage.concat(this.props.filters ? this.props.filters : []);
    if (error.msg) {
      return judge.includes(error.msg);
    }
    return true;
  };

  filter = (error: ErrorInfo) => {
    // filter by user define
    if (this.beforeFilter(error)) {
      return;
    }
    // filter the mutiple items
    const { localtime, caughtEvent } = error;
    const label = `${Math.floor((localtime as number) / 1000)}-${caughtEvent}`;
    error.localtime = this.getTime(localtime);
    this.state.maps.set(label, error);
    this.debounce();
  };
  // 过滤1秒内连续抛出同一错误
  debounce = () => {
    try {
      if (this.timeout !== null) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        const max = this.props.max || 1;
        if (this.state.maps && this.state.maps.size >= max) {
          this.catchBack();
        }
      }, 1000);
    } catch (error) {
      console.log(`${this.selfError}debounce`, error);
    }
  };

  catchBack = () => {
    try {
      const report: ReportError = {
        level: 'error',
        app: this.props.app || 'pay_app',
        errors: Array.from(this.state.maps.values()),
        localinfo: {
          user: this.props.user || 'pay_user',
          ...(this.props.token ? { token: this.props.token } : {}),
          ...(this.props.language ? { user_language: this.props.language } : {}),
          ua: window.navigator.userAgent,
          is_cookie: window.navigator.cookieEnabled ? 1 : 0,
          cookie: document.cookie || '',
          screen_height: window.screen.availHeight,
          screen_width: window.screen.availWidth,
        },
      };
      if (this.props.onCatch) {
        this.props.onCatch(report);
      }
      // after callback the maps, then clear
      this.state.maps.clear();
    } catch (error) {
      console.log(`${this.selfError}catchBack`, error);
    }
  };

  postError = (error: ErrorInfo) => {
    if (process.env.NODE_ENV === 'development') {
      console.table(error);
    }
    // filter same errors, it will remian the last one
    this.filter(error);
  };

  catchError = (error: ErrorEvent) => {
    error.stopPropagation();
    try {
      const { colno, lineno, filename, type, isTrusted, message } = error;
      const obj = {
        caughtEvent: 'onerror',
        msg: message,
        localtime: Date.now(),
        stack: `Error: at ${filename} ${lineno}:${colno}`,
        eventType: type,
        isTrusted: isTrusted ? 1 : 0,
        errHref: window.location.href,
      };
      this.postError(obj);
      console.log('🚀 ~ file: index.tsx:142 ~ ErrorBoundary ~ obj:', obj);
    } catch (error) {
      console.log(`${this.selfError}catchError`, error);
    }
  };

  catchRejectEvent = (error: PromiseRejectionEvent) => {
    try {
      const { type, reason, isTrusted } = error;
      let msg;
      let stack;
      if (typeof reason === 'string') {
        msg = reason;
      }
      if (Object.prototype.toString.call(reason) === '[object Error]') {
        msg = reason.message;
        stack = JSON.stringify(reason);
        if (stack === '{}') {
          stack = reason.stack;
        }
        // 防止上报日志时接口出错，造成死循环
        if (reason.config?.url?.includes('/log/web/report') || reason.config?.url?.includes('publiclog')) {
          return;
        }
      }
      const obj = {
        caughtEvent: 'onunhandledrejection',
        msg,
        localtime: Date.now(),
        stack,
        eventType: type,
        isTrusted: isTrusted ? 1 : 0,
        errHref: window.location.href,
      };
      this.postError(obj);
    } catch (error) {
      console.log(`${this.selfError}catchRejectEvent`, error);
    }
    error.stopPropagation();
  };

  privateZero(s: string | number) {
    return Number(s) < 10 ? `0${s}` : s;
  }
  getTime = (localtime: number | string) => {
    const date = new Date(localtime);
    const month: string | number = this.privateZero(1 + date.getMonth());
    return `${date.getFullYear()}-${month}-${this.privateZero(date.getDate())} ${this.privateZero(
      date.getHours(),
    )}:${this.privateZero(date.getMinutes())}:${this.privateZero(date.getSeconds())}`;
  };

  render() {
    const { errorRender } = this.props;
    if (this.state.hasError) {
      return errorRender ? errorRender : <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
